import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useRouter } from 'next/router'
import Head from 'next/head'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { useWebSocketContext } from '../context/WebSocketContext'
import MobileBackButton from '../components/MobileBackButton'
import PlanCreditsDisplay from '../components/PlanCreditsDisplay'
import EntryCard from '../components/dictionary/EntryCard'
import GroupedEntryCard from '../components/dictionary/GroupedEntryCard'
import groupDictionaryEntries from '../utils/groupDictionaryEntries'
import WordOfTheDayCard from '../components/dictionary/WordOfTheDayCard'
import AlphabetNav from '../components/dictionary/AlphabetNav'
import FilterBar from '../components/dictionary/FilterBar'
import Pagination from '../components/dictionary/Pagination'
import { useInsufficientCreditsAlert } from '../components/InsufficientCreditsAlert'
import EntryCardSkeleton from '../components/dictionary/EntryCardSkeleton'
import EnglishEntryCard from '../components/dictionary/EnglishEntryCard'
import FrenchEntryCard from '../components/dictionary/FrenchEntryCard'
import SpanishEntryCard from '../components/dictionary/SpanishEntryCard'
import PortugueseEntryCard from '../components/dictionary/PortugueseEntryCard'
import AlphabetTableCard from '../components/dictionary/AlphabetTableCard'

export default function DictionaryPage() {
  const router = useRouter()
  const { isAuthenticated, authToken, isStaff, loading: authLoading } = useAuth()
  const { theme } = useTheme()
  const { addListener, isConnected: wsConnected } = useWebSocketContext()
  const { showInsufficientCreditsAlert, AlertComponent } = useInsufficientCreditsAlert()

  // State
  const [entries, setEntries] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [currentPage, setCurrentPageRaw] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = parseInt(localStorage.getItem('dict_currentPage'), 10)
      return saved > 0 ? saved : 1
    }
    return 1
  })
  const setCurrentPage = (page) => {
    setCurrentPageRaw(page)
    if (typeof window !== 'undefined') {
      if (page > 1) {
        localStorage.setItem('dict_currentPage', String(page))
      } else {
        localStorage.removeItem('dict_currentPage')
      }
    }
  }
  const [totalCount, setTotalCount] = useState(0)
  const [hasNext, setHasNext] = useState(false)
  const [hasPrevious, setHasPrevious] = useState(false)
  const [selectedLetter, setSelectedLetterRaw] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('dict_selectedLetter') || null
    }
    return null
  })
  const setSelectedLetter = (letter) => {
    setSelectedLetterRaw(letter)
    if (typeof window !== 'undefined') {
      if (letter) {
        localStorage.setItem('dict_selectedLetter', letter)
      } else {
        localStorage.removeItem('dict_selectedLetter')
      }
    }
  }
  const [selectedPOS, setSelectedPOS] = useState(null)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [posCategories, setPOSCategories] = useState([])
  const [semanticCategories, setSemanticCategories] = useState([])
  const [favorites, setFavorites] = useState(new Set())
  const [wordOfTheDay, setWordOfTheDay] = useState(null)
  const [expandedEntry, setExpandedEntry] = useState(null)
  const [wotdExpanded, setWotdExpanded] = useState(false)
  const [dictionaryVersion, setDictionaryVersion] = useState('v4')
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false)
  const [diacriticMatch, setDiacriticMatch] = useState(false)
  const [wordMatch, setWordMatch] = useState(false)
  const [fuzzyMatch, setFuzzyMatch] = useState(false)
  // Mutual exclusion between Fuzzy match and Word/Diacritic match:
  // fuzzy's semantics ("spelled similarly") directly contradict word
  // match ("spelled EXACTLY like this") and diacritic-strict matching.
  // Wrapping the setter lets us clear the other two whenever fuzzy
  // flips on, so URL state, request state, and UI checkboxes never
  // fall into a "both are true" limbo.
  const setFuzzyMatchWithExclusion = (v) => {
    setFuzzyMatch(v)
    if (v) {
      setWordMatch(false)
      setDiacriticMatch(false)
    }
  }
  const setWordMatchWithExclusion = (v) => {
    setWordMatch(v)
    if (v) setFuzzyMatch(false)
  }
  const [showAlphabetTable, setShowAlphabetTableRaw] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('dict_showAlphabetTable') === 'true'
    }
    return false
  })
  const setShowAlphabetTable = (show) => {
    setShowAlphabetTableRaw(show)
    if (typeof window !== 'undefined') {
      if (show) {
        localStorage.setItem('dict_showAlphabetTable', 'true')
      } else {
        localStorage.removeItem('dict_showAlphabetTable')
      }
    }
  }

  // Tab state: 'creole-english' | 'english-creole' | 'french-creole' | 'spanish-creole' | 'portuguese-creole'
  const [activeTab, setActiveTab] = useState('creole-english')

  // English tab state
  const [englishEntries, setEnglishEntries] = useState([])
  const [englishStats, setEnglishStats] = useState(null)
  const [englishLoading, setEnglishLoading] = useState(false)
  const [englishTotalCount, setEnglishTotalCount] = useState(0)
  const [englishCurrentPage, setEnglishCurrentPage] = useState(1)
  const [englishHasNext, setEnglishHasNext] = useState(false)
  const [englishHasPrevious, setEnglishHasPrevious] = useState(false)
  const [englishSelectedLetter, setEnglishSelectedLetter] = useState(null)
  const [englishSelectedPOS, setEnglishSelectedPOS] = useState(null)
  const [englishSearchTerm, setEnglishSearchTerm] = useState('')
  const [englishDebouncedSearch, setEnglishDebouncedSearch] = useState('')

  // French tab state (mirrors English tab)
  const [frenchEntries, setFrenchEntries] = useState([])
  const [frenchStats, setFrenchStats] = useState(null)
  const [frenchLoading, setFrenchLoading] = useState(false)
  const [frenchTotalCount, setFrenchTotalCount] = useState(0)
  const [frenchCurrentPage, setFrenchCurrentPage] = useState(1)
  const [frenchHasNext, setFrenchHasNext] = useState(false)
  const [frenchHasPrevious, setFrenchHasPrevious] = useState(false)
  const [frenchSelectedLetter, setFrenchSelectedLetter] = useState(null)
  const [frenchSelectedPOS, setFrenchSelectedPOS] = useState(null)
  const [frenchSearchTerm, setFrenchSearchTerm] = useState('')
  const [frenchDebouncedSearch, setFrenchDebouncedSearch] = useState('')

  // Spanish tab state (mirrors French tab)
  const [spanishEntries, setSpanishEntries] = useState([])
  const [spanishStats, setSpanishStats] = useState(null)
  const [spanishLoading, setSpanishLoading] = useState(false)
  const [spanishTotalCount, setSpanishTotalCount] = useState(0)
  const [spanishCurrentPage, setSpanishCurrentPage] = useState(1)
  const [spanishHasNext, setSpanishHasNext] = useState(false)
  const [spanishHasPrevious, setSpanishHasPrevious] = useState(false)
  const [spanishSelectedLetter, setSpanishSelectedLetter] = useState(null)
  const [spanishSelectedPOS, setSpanishSelectedPOS] = useState(null)
  const [spanishSearchTerm, setSpanishSearchTerm] = useState('')
  const [spanishDebouncedSearch, setSpanishDebouncedSearch] = useState('')

  // Portuguese tab state (mirrors Spanish tab)
  const [portugueseEntries, setPortugueseEntries] = useState([])
  const [portugueseStats, setPortugueseStats] = useState(null)
  const [portugueseLoading, setPortugueseLoading] = useState(false)
  const [portugueseTotalCount, setPortugueseTotalCount] = useState(0)
  const [portugueseCurrentPage, setPortugueseCurrentPage] = useState(1)
  const [portugueseHasNext, setPortugueseHasNext] = useState(false)
  const [portugueseHasPrevious, setPortugueseHasPrevious] = useState(false)
  const [portugueseSelectedLetter, setPortugueseSelectedLetter] = useState(null)
  const [portugueseSelectedPOS, setPortugueseSelectedPOS] = useState(null)
  const [portugueseSearchTerm, setPortugueseSearchTerm] = useState('')
  const [portugueseDebouncedSearch, setPortugueseDebouncedSearch] = useState('')

  // TTS pronunciation state
  const [playingEntryId, setPlayingEntryId] = useState(null)
  const [ttsLoading, setTtsLoading] = useState({})
  const [ttsError, setTtsError] = useState({})
  const audioRef = useRef(null)
  const playingEntryIdRef = useRef(null)
  const pendingPronunciationRef = useRef(new Map())
  // Stale-response guard for the Haitian search fetch. Every fetchEntries()
  // stamps itself with an incrementing ID; if the ID no longer matches when
  // the response comes back, the response is stale and gets discarded before
  // setEntries fires. Complements the 300ms debouncedSearch: if the user
  // pauses between typing bursts long enough that debounce doesn't collapse
  // them, races between the resulting requests are still filtered out here.
  const latestHaitianFetchIdRef = useRef(0)
  const latestEnglishFetchIdRef = useRef(0)
  const latestFrenchFetchIdRef = useRef(0)
  const latestSpanishFetchIdRef = useRef(0)
  const latestPortugueseFetchIdRef = useRef(0)
  // Per-entry favorite-toggle epoch. Rapid star/unstar clicks on the same
  // entry fire multiple POSTs; whichever server response arrives LAST wins
  // the local Set state. If out-of-order, an older response overwrites the
  // newer one and UI disagrees with DB. Map: entryId → click counter,
  // incremented on each click; response only applies if its counter matches
  // the entry's latest counter.
  const latestFavoriteToggleRef = useRef(new Map())

  // Constants
  const DEFAULT_VOICE_ID = 'haitian-male-1'
  const HAITIAN_ALPHABET = ['A', 'B', 'Ch', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'R', 'S', 'T', 'U', 'V', 'W', 'Y', 'Z', '#']
  const ENGLISH_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')
  const ENGLISH_POS_CHOICES = [
    { value: 'noun', label: 'Noun' },
    { value: 'verb', label: 'Verb' },
    { value: 'adjective', label: 'Adjective' },
    { value: 'adverb', label: 'Adverb' },
    { value: 'preposition', label: 'Preposition' },
    { value: 'conjunction', label: 'Conjunction' },
    { value: 'pronoun', label: 'Pronoun' },
    { value: 'interjection', label: 'Interjection' },
    { value: 'phrase', label: 'Phrase' },
    { value: 'other', label: 'Other' },
  ]
  const PAGE_SIZE = 50

  // POS markers sorted by commonality
  const POS_COMMONALITY_ORDER = [
    'n.', 'v.', 'adj.', 'adv.', 'prep.', 'pron.', 'conj.', 'interj.',
    'prop.n.', 'f.prop.n.', 'art.', 'abbrev.', 'acron.', 'contr.',
    'prefix', 'suffix', 'f.n.', 'm.n.', 'f.', 'm.', 'onomat.',
    'see', 'cf.', 'also'
  ]

  const getPOSSortOrder = (posAbbrev) => {
    const lower = posAbbrev?.toLowerCase()
    const idx = POS_COMMONALITY_ORDER.findIndex(p => p.toLowerCase() === lower)
    return idx >= 0 ? idx : POS_COMMONALITY_ORDER.length
  }

  // Canonical POS abbreviation → full name mapping (used for tooltips)
  const POS_FULL_NAMES = {
    'n.': 'Noun', 'v.': 'Verb', 'adj.': 'Adjective', 'adv.': 'Adverb',
    'prep.': 'Preposition', 'pron.': 'Pronoun', 'conj.': 'Conjunction',
    'interj.': 'Interjection', 'art.': 'Article',
    'prop.n.': 'Proper Noun', 'f.prop.n.': 'Feminine Proper Noun',
    'abbrev.': 'Abbreviation', 'acron.': 'Acronym',
    'contr.': 'Contraction', 'prefix': 'Prefix', 'suffix': 'Suffix',
    'suffix to indicate ordinal number': 'Ordinal Suffix',
  }
  // Normalize any source (API UPPERCASE, Title Case, or static map) to consistent Title Case
  const toTitleCase = (s) => s ? s.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) : null
  const getPOSFullName = (abbrev) => {
    const key = abbrev?.toLowerCase()
    return POS_FULL_NAMES[key] || null
  }
  // Resolve full name from any source, always returning Title Case
  const resolvePOSFullName = (abbrev, apiFullName) => {
    return toTitleCase(apiFullName) || POS_FULL_NAMES[abbrev?.toLowerCase()] || null
  }

  // Helper to expand POS/regional abbreviations
  const REGIONAL_EXPANSIONS = {
    'N': 'Northern-Haiti', 'N.': 'Northern-Haiti',
    'S': 'Southern-Haiti', 'S.': 'Southern-Haiti',
    'PaP': 'Port-au-Prince',
    'reg': 'Regional', 'reg.': 'Regional',
    'Dept': 'Department', 'Dept.': 'Department',
  }
  const expandPOSLabel = (pos) => REGIONAL_EXPANSIONS[pos] || pos

  // Format POS abbreviation for pill display: remove trailing period, replace internal periods with hyphens
  const formatPOSPill = (pos) => {
    const label = expandPOSLabel(pos)
    return label.replace(/\.$/, '').replace(/\./g, '-')
  }

  // Helper to highlight search term in text (returns React elements)
  const highlightSearchTerm = (text, term) => {
    if (!term || !text) return text
    let cleanedTerm = term.replace(/^\^/, '').replace(/\$$/, '')
    if (!cleanedTerm) return text
    try {
      const escapedTerm = cleanedTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const regex = new RegExp(`(${escapedTerm})`, 'gi')
      const parts = text.split(regex)
      if (parts.length === 1) return text
      return parts.map((part, i) => {
        if (part.toLowerCase() === cleanedTerm.toLowerCase()) {
          return <mark key={i} className="bg-yellow-200 dark:bg-yellow-600 text-gray-900 dark:text-white px-0.5 rounded">{part}</mark>
        }
        return part
      })
    } catch (e) {
      return text
    }
  }

  // Helper to highlight search term in HTML string
  const highlightSearchTermInHtml = (html, term) => {
    if (!term || !html) return html
    let cleanedTerm = term.replace(/^\^/, '').replace(/\$$/, '')
    if (!cleanedTerm) return html
    try {
      const escapedTerm = cleanedTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const regex = new RegExp(`(${escapedTerm})(?![^<]*>)`, 'gi')
      return html.replace(regex, '<mark class="bg-yellow-200 dark:bg-yellow-600 text-gray-900 dark:text-white px-0.5 rounded">$1</mark>')
    } catch (e) {
      return html
    }
  }

  // POS color classes for badges
  const getPosColorClass = (posAbbrev, isDark = false) => {
    const pos = posAbbrev?.toLowerCase()
    if (['f.n.', 'f.', 'f.prop.n.', 'f.adj.', 'f.prop.'].includes(pos)) {
      return isDark
        ? 'bg-pink-900/40 text-pink-300 border-pink-700'
        : 'bg-pink-50 text-pink-800 border-pink-300'
    }
    if (['m.n.', 'm.', 'm.prop.n.', 'm.adj.', 'm.prop.'].includes(pos)) {
      return isDark
        ? 'bg-sky-900/40 text-sky-300 border-sky-700'
        : 'bg-sky-50 text-sky-800 border-sky-300'
    }
    return isDark
      ? 'bg-blue-900/40 text-blue-300 border-blue-700'
      : 'bg-blue-50 text-blue-800 border-blue-300'
  }

  // ==================== Effects ====================

  // Load dictionary version from localStorage. Any non-v4 value is
  // migrated to v4 unconditionally — the migration is idempotent
  // (just a write) so guarding it with a separate dictMigrationVersion
  // flag was unnecessary and left a stranded-state bug where a user
  // with dictionaryVersion=v3 AND dictMigrationVersion=2 (from a
  // partial storage clear) would restore v3. dictMigrationVersion is
  // still written for continuity but no longer gates the migration.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('dictionaryVersion')
      if (saved && saved !== 'v4') {
        localStorage.setItem('dictionaryVersion', 'v4')
        localStorage.setItem('dictMigrationVersion', '2')
        setDictionaryVersion('v4')
      } else if (saved) {
        setDictionaryVersion(saved)
      }
    }
  }, [])

  // Listen for storage changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'dictionaryVersion' && e.newValue) setDictionaryVersion(e.newValue)
    }
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  // Persist and restore scroll position across navigation
  const scrollYRef = useRef(0)
  const scrollRestoredRef = useRef(false)

  // Track scroll on the <main> container (Layout.js uses overflow-y-auto on <main>)
  useEffect(() => {
    const main = document.querySelector('main')
    if (!main) return
    const onScroll = () => { scrollYRef.current = main.scrollTop }
    main.addEventListener('scroll', onScroll, { passive: true })
    return () => main.removeEventListener('scroll', onScroll)
  }, [])

  // Save scroll to localStorage on route change (fires before scroll resets)
  useEffect(() => {
    const onRouteChange = () => {
      localStorage.setItem('dict_scrollY', String(scrollYRef.current))
    }
    router.events.on('routeChangeStart', onRouteChange)
    return () => router.events.off('routeChangeStart', onRouteChange)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Restore scroll once after entries load
  useEffect(() => {
    if (loading || scrollRestoredRef.current) return
    scrollRestoredRef.current = true
    const saved = parseInt(localStorage.getItem('dict_scrollY'), 10)
    if (saved > 0) {
      requestAnimationFrame(() => {
        const main = document.querySelector('main')
        if (main) main.scrollTop = saved
      })
    }
  }, [loading])

  // WebSocket listener for pronunciation updates
  useEffect(() => {
    if (!addListener) return
    const removeListener = addListener('dictionary-pronunciation', (data) => {
      if (data.event === 'dictionary_pronunciation_ready' && data.entry_id && data.audio_url) {
        const wsEntryId = Number(data.entry_id)
        const isVariantWs = !!data.variant_word
        const isExampleWs = data.example_index !== undefined && data.example_index !== null
        const isUsageWs = data.usage_index !== undefined && data.usage_index !== null

        // Determine session cache key and pending promise key
        let cacheKey, pendingKey
        if (isUsageWs) {
          const uIdx = Number(data.usage_index)
          cacheKey = uIdx >= 900 ? `sub:${uIdx - 900}` : `usage:${uIdx}`
          pendingKey = `${wsEntryId}:usage:${uIdx}`
        } else if (isExampleWs) {
          const exIdx = Number(data.example_index)
          cacheKey = `ex:${exIdx}`
          pendingKey = `${wsEntryId}:ex:${exIdx}`
        } else if (isVariantWs) {
          cacheKey = `var:${data.variant_word}`
          pendingKey = `${wsEntryId}:${data.variant_word}`
        } else {
          cacheKey = 'main'
          pendingKey = wsEntryId
        }

        // Cache audio URL in session state
        setEntries(prev => prev.map(e => {
          if (Number(e.id) !== wsEntryId) return e
          return {
            ...e,
            _sessionAudio: { ...(e._sessionAudio || {}), [cacheKey]: data.audio_url }
          }
        }))

        // Also update word-of-the-day if it's the main word
        if (!isUsageWs && !isExampleWs && !isVariantWs) {
          setWordOfTheDay(prev => {
            if (prev?.entry?.id && Number(prev.entry.id) === wsEntryId) {
              return {
                ...prev,
                entry: {
                  ...prev.entry,
                  _sessionAudio: { ...(prev.entry._sessionAudio || {}), main: data.audio_url }
                }
              }
            }
            return prev
          })
        }

        // Resolve pending promise
        const pending = pendingPronunciationRef.current.get(pendingKey)
        if (pending) {
          pending.resolve(data.audio_url)
          pendingPronunciationRef.current.delete(pendingKey)
        }
      }
    })
    return removeListener
  }, [addListener])

  // Initialize tab and search from URL
  useEffect(() => {
    if (!router.isReady) return
    if (router.query.tab === 'english') setActiveTab('english-creole')
    if (router.query.tab === 'french') setActiveTab('french-creole')
    if (router.query.tab === 'spanish') setActiveTab('spanish-creole')
    if (router.query.tab === 'portuguese') setActiveTab('portuguese-creole')
    if (router.query.search) setSearchTerm(router.query.search)
    // Accept both `word_match=true` (current, matches the API param)
    // and `word_match=1` (legacy — bookmarks/links from before we
    // aligned URL and API to use the same serialization).
    const wm = router.query.word_match
    setWordMatch(wm === 'true' || wm === '1')
    const fm = router.query.fuzzy_match
    setFuzzyMatch(fm === 'true' || fm === '1')
  }, [router.isReady, router.query.search, router.query.tab, router.query.word_match, router.query.fuzzy_match])

  // State → URL sync for wordMatch. Without this, toggling the
  // checkbox left the URL out of sync with actual state, so a
  // bookmarked/shared URL would silently reflect a different flag
  // value than the one currently active in the tab. shallow:true
  // avoids re-firing the initial data-load effects.
  useEffect(() => {
    if (!router.isReady) return
    const params = new URLSearchParams(window.location.search)
    const current = params.get('word_match')
    if (wordMatch) {
      if (current === 'true') return
      params.set('word_match', 'true')
    } else {
      if (current == null) return
      params.delete('word_match')
    }
    const qs = params.toString()
    const newUrl = qs ? `/dictionary?${qs}` : '/dictionary'
    router.replace(newUrl, undefined, { shallow: true })
  }, [wordMatch, router.isReady])

  // State → URL sync for fuzzyMatch (mirrors the wordMatch sync above).
  useEffect(() => {
    if (!router.isReady) return
    const params = new URLSearchParams(window.location.search)
    const current = params.get('fuzzy_match')
    if (fuzzyMatch) {
      if (current === 'true') return
      params.set('fuzzy_match', 'true')
    } else {
      if (current == null) return
      params.delete('fuzzy_match')
    }
    const qs = params.toString()
    const newUrl = qs ? `/dictionary?${qs}` : '/dictionary'
    router.replace(newUrl, undefined, { shallow: true })
  }, [fuzzyMatch, router.isReady])

  // Debounce search (Creole tab)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Debounce search (English tab)
  useEffect(() => {
    const timer = setTimeout(() => setEnglishDebouncedSearch(englishSearchTerm), 300)
    return () => clearTimeout(timer)
  }, [englishSearchTerm])

  // Debounce search (French tab)
  useEffect(() => {
    const timer = setTimeout(() => setFrenchDebouncedSearch(frenchSearchTerm), 300)
    return () => clearTimeout(timer)
  }, [frenchSearchTerm])

  // Debounce search (Spanish tab)
  useEffect(() => {
    const timer = setTimeout(() => setSpanishDebouncedSearch(spanishSearchTerm), 300)
    return () => clearTimeout(timer)
  }, [spanishSearchTerm])

  // Debounce search (Portuguese tab)
  useEffect(() => {
    const timer = setTimeout(() => setPortugueseDebouncedSearch(portugueseSearchTerm), 300)
    return () => clearTimeout(timer)
  }, [portugueseSearchTerm])

  // Auth check and initial data fetch
  useEffect(() => {
    if (authLoading) return
    if (!isAuthenticated) {
      router.push('/login?redirect=/dictionary')
      return
    }
    fetchStats()
    fetchPOSCategories()
    fetchSemanticCategories()
    fetchFavorites()
    fetchWordOfTheDay()
    const savedPage = parseInt(localStorage.getItem('dict_currentPage'), 10)
    fetchEntries(savedPage > 0 ? savedPage : 1)
    // Mark the initial-load quota as consumed here — the filter-change
    // effect below early-returned during the pre-auth mount without
    // touching its ref (auth wasn't ready yet), so the user's first
    // real search would otherwise be swallowed by the "skip once"
    // guard. Consuming the ref at the moment of the actual initial
    // fetch makes the filter effect a normal listener from then on.
    isInitialFilterLoad.current = false
  }, [isAuthenticated, authLoading])

  // Re-fetch when Creole filters change
  const isInitialFilterLoad = useRef(true)
  useEffect(() => {
    if (!isAuthenticated || authLoading) return
    if (activeTab !== 'creole-english') return
    if (isInitialFilterLoad.current) {
      isInitialFilterLoad.current = false
      return // skip — initial load already fetched with saved page
    }
    // Clear any expanded card before refetching. Otherwise an entry
    // that happens to share the same ID as the previously-expanded
    // one would auto-expand into the new result set, which reads as
    // a UI bug ("why is this random entry open?").
    setExpandedEntry(null)
    fetchEntries(1)
  }, [debouncedSearch, selectedLetter, selectedPOS, selectedCategory, dictionaryVersion, showFavoritesOnly, diacriticMatch, wordMatch, fuzzyMatch, activeTab])

  // Fetch English entries when English tab is active and filters change
  useEffect(() => {
    if (!isAuthenticated || authLoading) return
    if (activeTab !== 'english-creole') return
    fetchEnglishEntries(1)
  }, [englishDebouncedSearch, englishSelectedLetter, englishSelectedPOS, dictionaryVersion, fuzzyMatch, activeTab])

  // Fetch French entries when French tab is active and filters change
  useEffect(() => {
    if (!isAuthenticated || authLoading) return
    if (activeTab !== 'french-creole') return
    fetchFrenchEntries(1)
  }, [frenchDebouncedSearch, frenchSelectedLetter, frenchSelectedPOS, dictionaryVersion, fuzzyMatch, activeTab])

  // Fetch Spanish entries when Spanish tab is active and filters change
  useEffect(() => {
    if (!isAuthenticated || authLoading) return
    if (activeTab !== 'spanish-creole') return
    fetchSpanishEntries(1)
  }, [spanishDebouncedSearch, spanishSelectedLetter, spanishSelectedPOS, dictionaryVersion, fuzzyMatch, activeTab])

  // Fetch Portuguese entries when Portuguese tab is active and filters change
  useEffect(() => {
    if (!isAuthenticated || authLoading) return
    if (activeTab !== 'portuguese-creole') return
    fetchPortugueseEntries(1)
  }, [portugueseDebouncedSearch, portugueseSelectedLetter, portugueseSelectedPOS, dictionaryVersion, fuzzyMatch, activeTab])

  // ==================== Data Fetching ====================

  const navigateToSearch = useCallback((term, options = {}) => {
    const trimmedTerm = term.trim()
    const useWordMatch = !!options.wordMatch
    if (trimmedTerm) {
      const params = new URLSearchParams({ search: trimmedTerm })
      if (useWordMatch) params.set('word_match', 'true')
      router.push(`/dictionary?${params.toString()}`, undefined, { shallow: true })
    } else {
      router.push('/dictionary', undefined, { shallow: true })
    }
    // Clear filters so the search isn't restricted by letter/POS/category
    setSelectedLetter(null)
    setSelectedPOS(null)
    setSelectedCategory(null)
    setShowFavoritesOnly(false)
    setWordMatch(useWordMatch)
    setSearchTerm(trimmedTerm)
  }, [router])

  const fetchStats = async () => {
    try {
      const api = (await import('../services/api')).default
      const response = await api.get(`/v1/dictionaries/public/stats/?dictionary_version=${dictionaryVersion}`)
      setStats(response.data)
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }

  const fetchPOSCategories = async () => {
    try {
      const api = (await import('../services/api')).default
      const response = await api.get(`/v1/dictionaries/public/pos_categories/?dictionary_version=${dictionaryVersion}`)
      setPOSCategories(response.data)
    } catch (error) {
      console.error('Error fetching POS categories:', error)
    }
  }

  const fetchSemanticCategories = async () => {
    try {
      const api = (await import('../services/api')).default
      const response = await api.get(`/v1/dictionaries/public/categories/?dictionary_version=${dictionaryVersion}`)
      setSemanticCategories(response.data)
    } catch (error) {
      console.error('Error fetching categories:', error)
    }
  }

  const fetchFavorites = async () => {
    try {
      const api = (await import('../services/api')).default
      const response = await api.get('/v1/dictionaries/favorites/')
      const favoriteWords = new Set(response.data.map(entry => entry.main_word?.toLowerCase()))
      setFavorites(favoriteWords)
    } catch (error) {
      console.error('Error fetching favorites:', error)
    }
  }

  const fetchWordOfTheDay = async () => {
    try {
      const api = (await import('../services/api')).default
      const response = await api.get('/v1/dictionaries/word-of-day/')
      if (response.data?.entry) setWordOfTheDay(response.data)
    } catch (error) {
      console.error('Error fetching word of the day:', error)
    }
  }

  const fetchEntries = async (page) => {
    const fetchId = ++latestHaitianFetchIdRef.current
    setLoading(true)
    try {
      const api = (await import('../services/api')).default
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: PAGE_SIZE.toString(),
        dictionary_version: dictionaryVersion
      })
      if (debouncedSearch) params.append('search', debouncedSearch)
      if (selectedLetter) params.append('letter', selectedLetter)
      if (selectedPOS) params.append('pos', selectedPOS)
      if (selectedCategory) params.append('category', selectedCategory)
      if (showFavoritesOnly) params.append('favorites_only', 'true')
      if (diacriticMatch) params.append('diacritic_match', 'true')
      if (wordMatch) params.append('word_match', 'true')
      if (fuzzyMatch) params.append('fuzzy_match', 'true')

      const response = await api.get(`/v1/dictionaries/public/?${params.toString()}`)
      // Discard if a newer fetch has since started — its response will
      // set state instead, and we don't want to overwrite it with ours.
      if (fetchId !== latestHaitianFetchIdRef.current) return
      setEntries(response.data.results || [])
      setTotalCount(response.data.count || 0)
      setHasNext(!!response.data.next)
      setHasPrevious(!!response.data.previous)
      setCurrentPage(page)
    } catch (error) {
      if (fetchId !== latestHaitianFetchIdRef.current) return
      console.error('Error fetching entries:', error)
      setEntries([])
    } finally {
      if (fetchId === latestHaitianFetchIdRef.current) {
        setLoading(false)
      }
    }
  }

  const fetchEnglishEntries = async (page) => {
    const fetchId = ++latestEnglishFetchIdRef.current
    setEnglishLoading(true)
    try {
      const api = (await import('../services/api')).default
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: PAGE_SIZE.toString(),
        dictionary_version: dictionaryVersion
      })
      if (englishDebouncedSearch) params.append('search', englishDebouncedSearch)
      if (englishSelectedLetter) params.append('letter', englishSelectedLetter)
      if (englishSelectedPOS) params.append('pos', englishSelectedPOS)
      if (fuzzyMatch) params.append('fuzzy_match', 'true')

      const response = await api.get(`/v1/dictionaries/english/?${params.toString()}`)
      // Discard if a newer fetch has since started.
      if (fetchId !== latestEnglishFetchIdRef.current) return
      // Group adjacent entries by english_word into combined cards
      const raw = response.data.results || []
      const grouped = []
      for (const entry of raw) {
        const last = grouped[grouped.length - 1]
        if (last && last.english_word === entry.english_word) {
          last.sections.push({
            part_of_speech: entry.part_of_speech,
            translations: entry.translations || [],
          })
          last.total_translations += (entry.translations || []).length
        } else {
          grouped.push({
            english_word: entry.english_word,
            sections: [{
              part_of_speech: entry.part_of_speech,
              translations: entry.translations || [],
            }],
            total_translations: (entry.translations || []).length,
          })
        }
      }
      setEnglishEntries(grouped)
      setEnglishTotalCount(response.data.count || 0)
      setEnglishHasNext(!!response.data.next)
      setEnglishHasPrevious(!!response.data.previous)
      setEnglishCurrentPage(page)
    } catch (error) {
      if (fetchId !== latestEnglishFetchIdRef.current) return
      console.error('Error fetching English entries:', error)
      setEnglishEntries([])
    } finally {
      if (fetchId === latestEnglishFetchIdRef.current) {
        setEnglishLoading(false)
      }
    }
  }

  const fetchEnglishStats = async () => {
    try {
      const api = (await import('../services/api')).default
      const response = await api.get(`/v1/dictionaries/english/stats/?dictionary_version=${dictionaryVersion}`)
      setEnglishStats(response.data)
    } catch (error) {
      console.error('Error fetching English stats:', error)
    }
  }

  const fetchFrenchEntries = async (page) => {
    const fetchId = ++latestFrenchFetchIdRef.current
    setFrenchLoading(true)
    try {
      const api = (await import('../services/api')).default
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: PAGE_SIZE.toString(),
        dictionary_version: dictionaryVersion
      })
      if (frenchDebouncedSearch) params.append('search', frenchDebouncedSearch)
      if (frenchSelectedLetter) params.append('letter', frenchSelectedLetter)
      if (frenchSelectedPOS) params.append('pos', frenchSelectedPOS)
      if (fuzzyMatch) params.append('fuzzy_match', 'true')

      const response = await api.get(`/v1/dictionaries/french/?${params.toString()}`)
      if (fetchId !== latestFrenchFetchIdRef.current) return
      // Group adjacent entries by french_word (identical to English pattern).
      const raw = response.data.results || []
      const grouped = []
      for (const entry of raw) {
        const last = grouped[grouped.length - 1]
        if (last && last.french_word === entry.french_word) {
          last.sections.push({
            part_of_speech: entry.part_of_speech,
            translations: entry.translations || [],
          })
          last.total_translations += (entry.translations || []).length
        } else {
          grouped.push({
            french_word: entry.french_word,
            sections: [{
              part_of_speech: entry.part_of_speech,
              translations: entry.translations || [],
            }],
            total_translations: (entry.translations || []).length,
          })
        }
      }
      setFrenchEntries(grouped)
      setFrenchTotalCount(response.data.count || 0)
      setFrenchHasNext(!!response.data.next)
      setFrenchHasPrevious(!!response.data.previous)
      setFrenchCurrentPage(page)
    } catch (error) {
      if (fetchId !== latestFrenchFetchIdRef.current) return
      console.error('Error fetching French entries:', error)
      setFrenchEntries([])
    } finally {
      if (fetchId === latestFrenchFetchIdRef.current) {
        setFrenchLoading(false)
      }
    }
  }

  const fetchFrenchStats = async () => {
    try {
      const api = (await import('../services/api')).default
      const response = await api.get(`/v1/dictionaries/french/stats/?dictionary_version=${dictionaryVersion}`)
      setFrenchStats(response.data)
    } catch (error) {
      console.error('Error fetching French stats:', error)
    }
  }

  const fetchSpanishEntries = async (page) => {
    const fetchId = ++latestSpanishFetchIdRef.current
    setSpanishLoading(true)
    try {
      const api = (await import('../services/api')).default
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: PAGE_SIZE.toString(),
        dictionary_version: dictionaryVersion
      })
      if (spanishDebouncedSearch) params.append('search', spanishDebouncedSearch)
      if (spanishSelectedLetter) params.append('letter', spanishSelectedLetter)
      if (spanishSelectedPOS) params.append('pos', spanishSelectedPOS)
      if (fuzzyMatch) params.append('fuzzy_match', 'true')

      const response = await api.get(`/v1/dictionaries/spanish/?${params.toString()}`)
      if (fetchId !== latestSpanishFetchIdRef.current) return
      const raw = response.data.results || []
      const grouped = []
      for (const entry of raw) {
        const last = grouped[grouped.length - 1]
        if (last && last.spanish_word === entry.spanish_word) {
          last.sections.push({
            part_of_speech: entry.part_of_speech,
            translations: entry.translations || [],
          })
          last.total_translations += (entry.translations || []).length
        } else {
          grouped.push({
            spanish_word: entry.spanish_word,
            sections: [{
              part_of_speech: entry.part_of_speech,
              translations: entry.translations || [],
            }],
            total_translations: (entry.translations || []).length,
          })
        }
      }
      setSpanishEntries(grouped)
      setSpanishTotalCount(response.data.count || 0)
      setSpanishHasNext(!!response.data.next)
      setSpanishHasPrevious(!!response.data.previous)
      setSpanishCurrentPage(page)
    } catch (error) {
      if (fetchId !== latestSpanishFetchIdRef.current) return
      console.error('Error fetching Spanish entries:', error)
      setSpanishEntries([])
    } finally {
      if (fetchId === latestSpanishFetchIdRef.current) {
        setSpanishLoading(false)
      }
    }
  }

  const fetchSpanishStats = async () => {
    try {
      const api = (await import('../services/api')).default
      const response = await api.get(`/v1/dictionaries/spanish/stats/?dictionary_version=${dictionaryVersion}`)
      setSpanishStats(response.data)
    } catch (error) {
      console.error('Error fetching Spanish stats:', error)
    }
  }

  const fetchPortugueseEntries = async (page) => {
    const fetchId = ++latestPortugueseFetchIdRef.current
    setPortugueseLoading(true)
    try {
      const api = (await import('../services/api')).default
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: PAGE_SIZE.toString(),
        dictionary_version: dictionaryVersion
      })
      if (portugueseDebouncedSearch) params.append('search', portugueseDebouncedSearch)
      if (portugueseSelectedLetter) params.append('letter', portugueseSelectedLetter)
      if (portugueseSelectedPOS) params.append('pos', portugueseSelectedPOS)
      if (fuzzyMatch) params.append('fuzzy_match', 'true')

      const response = await api.get(`/v1/dictionaries/portuguese/?${params.toString()}`)
      if (fetchId !== latestPortugueseFetchIdRef.current) return
      const raw = response.data.results || []
      const grouped = []
      for (const entry of raw) {
        const last = grouped[grouped.length - 1]
        if (last && last.portuguese_word === entry.portuguese_word) {
          last.sections.push({
            part_of_speech: entry.part_of_speech,
            translations: entry.translations || [],
          })
          last.total_translations += (entry.translations || []).length
        } else {
          grouped.push({
            portuguese_word: entry.portuguese_word,
            sections: [{
              part_of_speech: entry.part_of_speech,
              translations: entry.translations || [],
            }],
            total_translations: (entry.translations || []).length,
          })
        }
      }
      setPortugueseEntries(grouped)
      setPortugueseTotalCount(response.data.count || 0)
      setPortugueseHasNext(!!response.data.next)
      setPortugueseHasPrevious(!!response.data.previous)
      setPortugueseCurrentPage(page)
    } catch (error) {
      if (fetchId !== latestPortugueseFetchIdRef.current) return
      console.error('Error fetching Portuguese entries:', error)
      setPortugueseEntries([])
    } finally {
      if (fetchId === latestPortugueseFetchIdRef.current) {
        setPortugueseLoading(false)
      }
    }
  }

  const fetchPortugueseStats = async () => {
    try {
      const api = (await import('../services/api')).default
      const response = await api.get(`/v1/dictionaries/portuguese/stats/?dictionary_version=${dictionaryVersion}`)
      setPortugueseStats(response.data)
    } catch (error) {
      console.error('Error fetching Portuguese stats:', error)
    }
  }

  // ==================== Actions ====================

  const toggleFavorite = async (entryId, mainWord) => {
    // Stamp this click. If a newer click on the same entry starts before
    // this response returns, discard our response — the newer click's
    // response is the truth. Without this, rapid star/unstar clicks can
    // land server responses out of order and leave the UI showing the
    // opposite of the actual DB state.
    const counter = (latestFavoriteToggleRef.current.get(entryId) || 0) + 1
    latestFavoriteToggleRef.current.set(entryId, counter)
    try {
      const api = (await import('../services/api')).default
      const response = await api.post('/v1/dictionaries/favorites/toggle/', { entry_id: entryId })
      if (latestFavoriteToggleRef.current.get(entryId) !== counter) return
      const wordLower = mainWord?.toLowerCase()
      setFavorites(prev => {
        const next = new Set(prev)
        if (response.data.is_favorited) next.add(wordLower)
        else next.delete(wordLower)
        return next
      })
    } catch (error) {
      if (latestFavoriteToggleRef.current.get(entryId) !== counter) return
      console.error('Error toggling favorite:', error)
    }
  }

  const clearFilters = () => {
    // Only clears the filter chips (letter, POS, category, favorites,
    // diacritic-match). Search term is preserved — user typed it and
    // wouldn't expect "Clear Filters" to wipe their query.
    if (activeTab === 'english-creole') {
      setEnglishSelectedLetter(null)
      setEnglishSelectedPOS(null)
    } else if (activeTab === 'french-creole') {
      setFrenchSelectedLetter(null)
      setFrenchSelectedPOS(null)
    } else if (activeTab === 'spanish-creole') {
      setSpanishSelectedLetter(null)
      setSpanishSelectedPOS(null)
    } else if (activeTab === 'portuguese-creole') {
      setPortugueseSelectedLetter(null)
      setPortugueseSelectedPOS(null)
    } else {
      setSelectedLetter(null)
      setSelectedPOS(null)
      setSelectedCategory(null)
      setShowFavoritesOnly(false)
      setDiacriticMatch(false)
      setWordMatch(false)
      setFuzzyMatch(false)
    }
  }

  const handleTabSwitch = (tab) => {
    setActiveTab(tab)
    // Reset BOTH pagination counters when switching tabs so the visible
    // pagination controls match the (about-to-be-fetched) page-1 data.
    // Without this, switching from Creole page 5 → English → back would
    // briefly show "Page 5" pagination against page-1 data before the
    // fetch success handler reset currentPage.
    setCurrentPage(1)
    setEnglishCurrentPage(1)
    setFrenchCurrentPage(1)
    setSpanishCurrentPage(1)
    setPortugueseCurrentPage(1)
    const urlParams = new URLSearchParams(window.location.search)
    if (tab === 'english-creole') {
      urlParams.set('tab', 'english')
      fetchEnglishStats()
    } else if (tab === 'french-creole') {
      urlParams.set('tab', 'french')
      fetchFrenchStats()
    } else if (tab === 'spanish-creole') {
      urlParams.set('tab', 'spanish')
      fetchSpanishStats()
    } else if (tab === 'portuguese-creole') {
      urlParams.set('tab', 'portuguese')
      fetchPortugueseStats()
    } else {
      urlParams.delete('tab')
    }
    const qs = urlParams.toString()
    router.replace(qs ? `/dictionary?${qs}` : '/dictionary', undefined, { shallow: true })
  }

  const handleCreoleClick = (creoleWord) => {
    setActiveTab('creole-english')
    setSelectedLetter(null)
    setSelectedPOS(null)
    setSelectedCategory(null)
    setShowFavoritesOnly(false)
    // Also clear wordMatch, diacriticMatch, and fuzzyMatch. The
    // searchTerm we're about to set is already an exact-match regex
    // (^word$); if wordMatch happened to be on from a previous
    // search, the fetch would wrap it AGAIN into ^^word$$ and produce
    // zero results. And fuzzyMatch would replace exact-match with a
    // similarity search, defeating the point of the drill-down.
    setWordMatch(false)
    setDiacriticMatch(false)
    setFuzzyMatch(false)
    setSearchTerm(`^${creoleWord}$`)
    router.replace(`/dictionary?search=${encodeURIComponent(`^${creoleWord}$`)}`, undefined, { shallow: true })
  }

  // Clear session audio cache for an entry (called after clear_audio API)
  const clearSessionAudio = (entryId) => {
    setEntries(prev => prev.map(e => {
      if (Number(e.id) !== Number(entryId)) return e
      const { _sessionAudio, ...rest } = e
      return rest
    }))
  }

  // Play pronunciation for a dictionary entry, one of its variants, or an example phrase.
  // textOverride: optional variant text to pronounce instead of main_word.
  // exampleIndex: optional 0-based index into creole_examples to pronounce an example phrase.
  // subEntryIndex: optional 0-based index for sub-entry example phrases.
  // Tracking key: entry.id (number) for main word, "${entryId}:${variant}" for variants,
  //   "${entryId}:ex:${index}" for examples, "${entryId}:sub:${index}" for sub-entries.
  const playPronunciation = async (entry, textOverride, exampleIndex, usageIndex, subEntryIndex, { forceRegenerate, pronunciationText } = {}) => {
    const entryId = Number(entry.id)
    const isSubEntry = subEntryIndex !== undefined && subEntryIndex !== null
    const isUsage = !isSubEntry && usageIndex !== undefined && usageIndex !== null
    const isVariant = !!textOverride && !isUsage && !isSubEntry
    const isExample = exampleIndex !== undefined && exampleIndex !== null
    const trackingKey = isSubEntry
      ? `${entryId}:sub:${subEntryIndex}`
      : isUsage
        ? `${entryId}:usage:${usageIndex}`
        : isExample
          ? `${entryId}:ex:${exampleIndex}`
          : isVariant ? `${entryId}:${textOverride}` : entryId

    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
    }
    if (playingEntryIdRef.current === trackingKey) {
      setPlayingEntryId(null)
      playingEntryIdRef.current = null
      return
    }
    setTtsLoading(prev => ({ ...prev, [trackingKey]: true }))
    setTtsError(prev => ({ ...prev, [trackingKey]: null }))
    setPlayingEntryId(trackingKey)
    playingEntryIdRef.current = trackingKey

    const api = (await import('../services/api')).default

    const playAudio = async (audioUrl) => {
      const audio = new Audio(audioUrl)
      audioRef.current = audio
      audio.onended = () => { setPlayingEntryId(null); playingEntryIdRef.current = null; audioRef.current = null }
      audio.onerror = () => {
        setTtsError(prev => ({ ...prev, [trackingKey]: 'Failed to play audio' }))
        setPlayingEntryId(null); playingEntryIdRef.current = null; audioRef.current = null
      }
      await audio.play()
      setTtsLoading(prev => ({ ...prev, [trackingKey]: false }))
    }

    try {
      // Check in-session cached audio (set after first API call in this session)
      // Audio URLs are NOT preloaded from the API — every user is charged at least once
      // Skip cache when forceRegenerate is true (staff bypass)
      if (forceRegenerate) {
        // Skip all cache checks
      } else if (isSubEntry) {
        const cachedUrl = entry._sessionAudio?.[`sub:${subEntryIndex}`]
        if (cachedUrl) { await playAudio(cachedUrl); return }
      } else if (isUsage) {
        const cachedUrl = entry._sessionAudio?.[`usage:${usageIndex}`]
        if (cachedUrl) { await playAudio(cachedUrl); return }
      } else if (isExample) {
        const cachedUrl = entry._sessionAudio?.[`ex:${exampleIndex}`]
        if (cachedUrl) { await playAudio(cachedUrl); return }
      } else if (isVariant) {
        const cachedUrl = entry._sessionAudio?.[`var:${textOverride}`]
        if (cachedUrl) { await playAudio(cachedUrl); return }
      } else {
        const cachedUrl = entry._sessionAudio?.main
        if (cachedUrl) { await playAudio(cachedUrl); return }
      }

      const postBody = isSubEntry
        ? { usage_text: textOverride, usage_index: 900 + subEntryIndex }
        : isUsage
          ? { usage_text: textOverride, usage_index: usageIndex }
          : isExample
            ? { example_index: exampleIndex }
            : isVariant ? { text: textOverride } : {}
      if (forceRegenerate) postBody.force_regenerate = true
      if (pronunciationText) postBody.pronunciation_text = pronunciationText

      // Add pipeline header for staff users (same as useTTSSubmit.js)
      const pipeline = localStorage.getItem('inferPipeline') || 'X'
      const pipelineHeaders = {}
      if (isStaff && pipeline === 'A') pipelineHeaders['X-Infer-PathA-Service'] = 'true'
      else if (isStaff && pipeline === 'B') pipelineHeaders['X-Infer-PathB-Service'] = 'true'
      else if (isStaff && pipeline === 'C') pipelineHeaders['X-Infer-PathC-Service'] = 'true'

      const result = await api.post(
        `/v1/dictionaries/public/${entryId}/generate-pronunciation/`,
        postBody,
        Object.keys(pipelineHeaders).length > 0 ? { headers: pipelineHeaders } : undefined
      )
      if (result.data.audio_url) {
        // Update the cached audio_url in state so subsequent clicks use cache
        // Cache in session state so same user doesn't get charged again this session
        const cacheKey = isSubEntry ? `sub:${subEntryIndex}`
          : isUsage ? `usage:${usageIndex}`
          : isExample ? `ex:${exampleIndex}`
          : isVariant ? `var:${textOverride}` : 'main'
        setEntries(prev => prev.map(e => {
          if (Number(e.id) !== entryId) return e
          return {
            ...e,
            _sessionAudio: { ...(e._sessionAudio || {}), [cacheKey]: result.data.audio_url }
          }
        }))
        await playAudio(result.data.audio_url)
        return
      }

      if (result.data.status === 'pending' || result.status === 202) {
        const pendingKey = isSubEntry ? `${entryId}:usage:${900 + subEntryIndex}` : isUsage ? trackingKey : isExample ? trackingKey : isVariant ? trackingKey : entryId
        const wsPromise = new Promise((resolve, reject) => {
          pendingPronunciationRef.current.set(pendingKey, { resolve, reject })
          setTimeout(() => {
            if (pendingPronunciationRef.current.has(pendingKey)) {
              pendingPronunciationRef.current.delete(pendingKey)
              reject(new Error('Timeout waiting for pronunciation'))
            }
          }, 90000)
        })

        const pollPromise = (async () => {
          // Only poll for main word (not examples/variants/usage — they rely on WebSocket)
          if (isExample || isVariant || isUsage || isSubEntry) {
            await new Promise(resolve => setTimeout(resolve, 90000))
            return null
          }
          for (let attempt = 0; attempt < 3; attempt++) {
            await new Promise(resolve => setTimeout(resolve, 30000))
            if (playingEntryIdRef.current !== trackingKey) return null
            if (!pendingPronunciationRef.current.has(pendingKey)) return null
            try {
              const checkResult = await api.get(`/v1/dictionaries/public/${entryId}/check-audio/`)
              if (checkResult.data.status === 'ready' && checkResult.data.audio_url) {
                pendingPronunciationRef.current.delete(pendingKey)
                return checkResult.data.audio_url
              }
            } catch (e) { /* continue polling */ }
          }
          return null
        })()

        const audioUrl = await Promise.race([wsPromise, pollPromise])
        if (audioUrl) {
          if (playingEntryIdRef.current !== trackingKey) return
          await playAudio(audioUrl)
          return
        }
        throw new Error('Audio generation failed or cancelled')
      }

      throw new Error('No audio URL returned')
    } catch (error) {
      const pendingKey = isUsage ? trackingKey : isExample ? trackingKey : isVariant ? trackingKey : entryId
      pendingPronunciationRef.current.delete(pendingKey)
      if (error.response?.status === 402) {
        showInsufficientCreditsAlert(error.response.data?.required || 0, error.response.data?.available || 0)
      } else {
        setTtsError(prev => ({ ...prev, [trackingKey]: error.message }))
      }
      setPlayingEntryId(null)
      playingEntryIdRef.current = null
      setTtsLoading(prev => ({ ...prev, [trackingKey]: false }))
    }
  }

  // ==================== Derived State ====================

  // Group adjacent Creole entries by main_word for homonym display
  const groupedEntries = useMemo(() => groupDictionaryEntries(entries), [entries])

  const totalPages = Math.ceil(totalCount / PAGE_SIZE)
  const englishTotalPages = Math.ceil(englishTotalCount / PAGE_SIZE)
  const frenchTotalPages = Math.ceil(frenchTotalCount / PAGE_SIZE)
  const spanishTotalPages = Math.ceil(spanishTotalCount / PAGE_SIZE)
  const portugueseTotalPages = Math.ceil(portugueseTotalCount / PAGE_SIZE)
  const isEnglishTab = activeTab === 'english-creole'
  const isFrenchTab = activeTab === 'french-creole'
  const isSpanishTab = activeTab === 'spanish-creole'
  const isPortugueseTab = activeTab === 'portuguese-creole'
  const isReverseTab = isEnglishTab || isFrenchTab || isSpanishTab || isPortugueseTab
  const hasActiveFilters = isEnglishTab
    ? !!(englishSearchTerm || englishSelectedLetter || englishSelectedPOS)
    : isFrenchTab
      ? !!(frenchSearchTerm || frenchSelectedLetter || frenchSelectedPOS)
      : isSpanishTab
        ? !!(spanishSearchTerm || spanishSelectedLetter || spanishSelectedPOS)
        : isPortugueseTab
          ? !!(portugueseSearchTerm || portugueseSelectedLetter || portugueseSelectedPOS)
          : !!(searchTerm || selectedLetter || selectedPOS || selectedCategory || showFavoritesOnly)

  // ==================== Render ====================

  if (authLoading) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-full bg-gray-50 dark:bg-dark-bg">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <>
      <Head>
        <title>Haitian Creole Dictionary | CreoleCentric</title>
        <meta name="description" content="Browse the comprehensive Haitian Creole to English dictionary with over 27,000 words and phrases." />
      </Head>

      <div className="flex-1 flex flex-col min-h-full bg-gray-50 dark:bg-dark-bg">
        <MobileBackButton />

        {/* Header */}
        <div className="bg-white dark:bg-dark-card border-b border-gray-200 dark:border-dark-border px-4 sm:px-6 lg:px-8 py-6">
          <div>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                  Haitian Creole Dictionary
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {isEnglishTab
                    ? (englishStats ? `${englishStats.total_entries?.toLocaleString()} English entries` : 'Loading...')
                    : isFrenchTab
                      ? (frenchStats ? `${frenchStats.total_entries?.toLocaleString()} French entries` : 'Loading...')
                      : isSpanishTab
                        ? (spanishStats ? `${spanishStats.total_entries?.toLocaleString()} Spanish entries` : 'Loading...')
                        : isPortugueseTab
                          ? (portugueseStats ? `${portugueseStats.total_entries?.toLocaleString()} Portuguese entries` : 'Loading...')
                          : (stats ? `${stats.total_entries?.toLocaleString()} words and phrases` : 'Loading...')}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <PlanCreditsDisplay />
                {!isReverseTab && favorites.size > 0 && (
                  <button
                    onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                    className={`flex items-center text-sm px-3 py-1.5 rounded-lg transition-colors ${
                      showFavoritesOnly
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 ring-2 ring-red-500'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    title={showFavoritesOnly ? 'Show all words' : 'Show favorites only'}
                  >
                    <svg className={`w-5 h-5 mr-1.5 ${showFavoritesOnly ? 'text-red-500' : 'text-red-400'}`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                    </svg>
                    {favorites.size} favorite{favorites.size !== 1 ? 's' : ''}
                    {showFavoritesOnly && (
                      <svg className="w-4 h-4 ml-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="w-full px-3 sm:px-4 lg:px-6 py-4 flex-1">
          {/* Tab Toggle — vertical dividers between segments, matching
              the per-cell border-r pattern the History tab grid uses
              (HistoryTabEnhanced.js:~4368). Last tab has no right-
              border so we don't get a rule on the trailing edge.
              Mobile: horizontal scroll (5 language pairs don't fit
              across a 375px viewport). Desktop (md+): equal-width
              flex-1 tabs so the strip fills the container.
              (2026-08-29 pt-BR-Kreyol tab addition user report.) */}
          <div className="flex mb-4 bg-gray-100 dark:bg-dark-border rounded-lg p-1 overflow-x-auto">
            <button
              onClick={() => handleTabSwitch('creole-english')}
              className={`flex-shrink-0 whitespace-nowrap md:flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors border-r border-gray-300 dark:border-gray-600 ${
                !isReverseTab
                  ? 'bg-white dark:bg-dark-card text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Kreyol &rarr; English
            </button>
            <button
              onClick={() => handleTabSwitch('english-creole')}
              className={`flex-shrink-0 whitespace-nowrap md:flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors border-r border-gray-300 dark:border-gray-600 ${
                isEnglishTab
                  ? 'bg-white dark:bg-dark-card text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              English &rarr; Kreyol
            </button>
            <button
              onClick={() => handleTabSwitch('french-creole')}
              className={`flex-shrink-0 whitespace-nowrap md:flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors border-r border-gray-300 dark:border-gray-600 ${
                isFrenchTab
                  ? 'bg-white dark:bg-dark-card text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Français &rarr; Kreyol
            </button>
            <button
              onClick={() => handleTabSwitch('spanish-creole')}
              className={`flex-shrink-0 whitespace-nowrap md:flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors border-r border-gray-300 dark:border-gray-600 ${
                isSpanishTab
                  ? 'bg-white dark:bg-dark-card text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Español &rarr; Kreyol
            </button>
            <button
              onClick={() => handleTabSwitch('portuguese-creole')}
              className={`flex-shrink-0 whitespace-nowrap md:flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                isPortugueseTab
                  ? 'bg-white dark:bg-dark-card text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Português &rarr; Kreyol
            </button>
          </div>

          {/* Word of the Day (Creole tab only) */}
          {!isReverseTab && wordOfTheDay && (
            <div className="mb-3">
            <WordOfTheDayCard
              wordOfTheDay={wordOfTheDay}
              expandedWotd={wotdExpanded}
              setExpandedWotd={setWotdExpanded}
              favorites={favorites}
              toggleFavorite={toggleFavorite}
              playPronunciation={playPronunciation}
              playingEntryId={playingEntryId}
              ttsLoading={ttsLoading}
              navigateToSearch={navigateToSearch}
              highlightSearchTerm={highlightSearchTerm}
              highlightSearchTermInHtml={highlightSearchTermInHtml}
              expandPOSLabel={expandPOSLabel}
              formatPOSPill={formatPOSPill}
              resolvePOSFullName={resolvePOSFullName}
              getPOSSortOrder={getPOSSortOrder}
            />
            </div>
          )}

          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <input
                type="text"
                value={isEnglishTab ? englishSearchTerm : isFrenchTab ? frenchSearchTerm : isSpanishTab ? spanishSearchTerm : isPortugueseTab ? portugueseSearchTerm : searchTerm}
                onChange={(e) => {
                  if (isEnglishTab) {
                    setEnglishSearchTerm(e.target.value)
                  } else if (isFrenchTab) {
                    setFrenchSearchTerm(e.target.value)
                  } else if (isSpanishTab) {
                    setSpanishSearchTerm(e.target.value)
                  } else if (isPortugueseTab) {
                    setPortugueseSearchTerm(e.target.value)
                  } else {
                    setSearchTerm(e.target.value)
                    if (showAlphabetTable) {
                      setShowAlphabetTable(false)
                    }
                  }
                }}
                placeholder={
                  isEnglishTab
                    ? 'Search English words...'
                    : isFrenchTab
                      ? 'Rechercher un mot français...'
                      : isSpanishTab
                        ? 'Buscar una palabra en español...'
                        : isPortugueseTab
                          ? 'Pesquisar uma palavra em português...'
                          : 'Search for words or definitions...'
                }
                className="w-full px-4 py-3 pl-10 pr-10 rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {(isEnglishTab ? englishSearchTerm : isFrenchTab ? frenchSearchTerm : isSpanishTab ? spanishSearchTerm : isPortugueseTab ? portugueseSearchTerm : searchTerm) && (
                <button
                  onClick={() => {
                    if (isEnglishTab) {
                      setEnglishSearchTerm('')
                    } else if (isFrenchTab) {
                      setFrenchSearchTerm('')
                    } else if (isSpanishTab) {
                      setSpanishSearchTerm('')
                    } else if (isPortugueseTab) {
                      setPortugueseSearchTerm('')
                    } else {
                      setSearchTerm('')
                      setDebouncedSearch('')
                      setSelectedLetter(null)
                      setSelectedPOS(null)
                      setSelectedCategory(null)
                      setShowFavoritesOnly(false)
                      setWordMatch(false)
                      setFuzzyMatch(false)
                      router.push('/dictionary', undefined, { shallow: true })
                    }
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Alphabet Navigation */}
          <AlphabetNav
            letters={isReverseTab ? ENGLISH_ALPHABET : HAITIAN_ALPHABET}
            selectedLetter={
              isEnglishTab
                ? englishSelectedLetter
                : isFrenchTab
                  ? frenchSelectedLetter
                  : isSpanishTab
                    ? spanishSelectedLetter
                    : isPortugueseTab
                      ? portugueseSelectedLetter
                      : (showAlphabetTable ? null : selectedLetter)
            }
            onSelectLetter={(letter) => {
              setShowAlphabetTable(false)
              if (isEnglishTab) {
                setEnglishSelectedLetter(letter)
                setEnglishSearchTerm('')
                setEnglishSelectedPOS(null)
              } else if (isFrenchTab) {
                setFrenchSelectedLetter(letter)
                setFrenchSearchTerm('')
                setFrenchSelectedPOS(null)
              } else if (isSpanishTab) {
                setSpanishSelectedLetter(letter)
                setSpanishSearchTerm('')
                setSpanishSelectedPOS(null)
              } else if (isPortugueseTab) {
                setPortugueseSelectedLetter(letter)
                setPortugueseSearchTerm('')
                setPortugueseSelectedPOS(null)
              } else {
                setSelectedLetter(letter)
                setSearchTerm('')
                setDebouncedSearch('')
                setSelectedPOS(null)
                setSelectedCategory(null)
                setShowFavoritesOnly(false)
                setDiacriticMatch(false)
                setWordMatch(false)
              }
            }}
            onAlphabetTable={!isReverseTab ? () => {
              setShowAlphabetTable(true)
              setSelectedLetter(null)
              setSearchTerm('')
              setDebouncedSearch('')
              setSelectedPOS(null)
              setSelectedCategory(null)
              setShowFavoritesOnly(false)
              setDiacriticMatch(false)
              setWordMatch(false)
            } : undefined}
            showAlphabetTable={showAlphabetTable}
          />

          {/* Filter Controls */}
          {isEnglishTab ? (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <select
                value={englishSelectedPOS || ''}
                onChange={(e) => setEnglishSelectedPOS(e.target.value || null)}
                className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card text-sm text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All parts of speech</option>
                {ENGLISH_POS_CHOICES.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : isFrenchTab ? (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <select
                value={frenchSelectedPOS || ''}
                onChange={(e) => setFrenchSelectedPOS(e.target.value || null)}
                className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card text-sm text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Toutes catégories</option>
                {ENGLISH_POS_CHOICES.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Effacer les filtres
                </button>
              )}
            </div>
          ) : isSpanishTab ? (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <select
                value={spanishSelectedPOS || ''}
                onChange={(e) => setSpanishSelectedPOS(e.target.value || null)}
                className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card text-sm text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todas las categorías</option>
                {ENGLISH_POS_CHOICES.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Limpiar filtros
                </button>
              )}
            </div>
          ) : isPortugueseTab ? (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <select
                value={portugueseSelectedPOS || ''}
                onChange={(e) => setPortugueseSelectedPOS(e.target.value || null)}
                className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card text-sm text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todas as categorias</option>
                {ENGLISH_POS_CHOICES.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Limpar filtros
                </button>
              )}
            </div>
          ) : (
            <FilterBar
              posCategories={posCategories}
              semanticCategories={semanticCategories}
              selectedPOS={selectedPOS}
              selectedCategory={selectedCategory}
              diacriticMatch={diacriticMatch}
              wordMatch={wordMatch}
              fuzzyMatch={fuzzyMatch}
              showSearchToggles={!!debouncedSearch}
              onChangePOS={(val) => setSelectedPOS(val || null)}
              onChangeCategory={(val) => setSelectedCategory(val || null)}
              onChangeDiacriticMatch={setDiacriticMatch}
              onChangeWordMatch={setWordMatchWithExclusion}
              onChangeFuzzyMatch={setFuzzyMatchWithExclusion}
              hasActiveFilters={hasActiveFilters}
              onClearFilters={clearFilters}
            />
          )}

          {/* Results Count */}
          <div className="mt-3 mb-4 text-xs text-gray-500 dark:text-gray-500 flex items-center justify-between">
            {(isEnglishTab ? englishLoading : isFrenchTab ? frenchLoading : isSpanishTab ? spanishLoading : isPortugueseTab ? portugueseLoading : loading) ? 'Loading...' : (
              <>
                {isEnglishTab ? (
                  <>
                    <span>
                      Showing {englishEntries.length} word{englishEntries.length !== 1 ? 's' : ''} ({englishTotalCount.toLocaleString()} entries)
                    </span>
                    {englishEntries.length > 0 && (
                      <span className="text-gray-500 dark:text-gray-500">
                        {englishEntries[0].english_word}{englishEntries.length > 1 ? ` \u2013 ${englishEntries[englishEntries.length - 1].english_word}` : ''}
                      </span>
                    )}
                  </>
                ) : isFrenchTab ? (
                  <>
                    <span>
                      {frenchEntries.length} mot{frenchEntries.length !== 1 ? 's' : ''} ({frenchTotalCount.toLocaleString()} entr\u00e9es)
                    </span>
                    {frenchEntries.length > 0 && (
                      <span className="text-gray-500 dark:text-gray-500">
                        {frenchEntries[0].french_word}{frenchEntries.length > 1 ? ` \u2013 ${frenchEntries[frenchEntries.length - 1].french_word}` : ''}
                      </span>
                    )}
                  </>
                ) : isSpanishTab ? (
                  <>
                    <span>
                      {spanishEntries.length} palabra{spanishEntries.length !== 1 ? 's' : ''} ({spanishTotalCount.toLocaleString()} entradas)
                    </span>
                    {spanishEntries.length > 0 && (
                      <span className="text-gray-500 dark:text-gray-500">
                        {spanishEntries[0].spanish_word}{spanishEntries.length > 1 ? ` \u2013 ${spanishEntries[spanishEntries.length - 1].spanish_word}` : ''}
                      </span>
                    )}
                  </>
                ) : isPortugueseTab ? (
                  <>
                    <span>
                      {portugueseEntries.length} palavra{portugueseEntries.length !== 1 ? 's' : ''} ({portugueseTotalCount.toLocaleString()} entradas)
                    </span>
                    {portugueseEntries.length > 0 && (
                      <span className="text-gray-500 dark:text-gray-500">
                        {portugueseEntries[0].portuguese_word}{portugueseEntries.length > 1 ? ` \u2013 ${portugueseEntries[portugueseEntries.length - 1].portuguese_word}` : ''}
                      </span>
                    )}
                  </>
                ) : showAlphabetTable ? (
                  <span>Haitian Creole Alphabet — 29 letters</span>
                ) : (
                  <>
                    <span>
                      Showing {groupedEntries.length} of {totalCount.toLocaleString()} {showFavoritesOnly ? 'favorited ' : ''}results
                      {showFavoritesOnly && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 align-middle">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                          </svg>
                          Favorites only
                        </span>
                      )}
                    </span>
                    {entries.length > 0 && (
                      <span className="text-gray-500 dark:text-gray-500">
                        {entries[0].main_word}{entries.length > 1 ? ` \u2013 ${entries[entries.length - 1].main_word}` : ''}
                      </span>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {/* Entry List */}
          {isEnglishTab ? (
            /* English Tab Content */
            englishLoading ? (
              <EntryCardSkeleton />
            ) : englishEntries.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-dark-card rounded-lg border border-gray-200 dark:border-dark-border">
                <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No words found</h3>
                <p className="text-gray-500 dark:text-gray-400">
                  {englishTotalCount === 0
                    ? 'The English dictionary has not been generated yet.'
                    : 'Try adjusting your search or filters'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <Pagination
                  currentPage={englishCurrentPage}
                  totalPages={englishTotalPages}
                  hasPrevious={englishHasPrevious}
                  hasNext={englishHasNext}
                  onPageChange={fetchEnglishEntries}
                />
                {englishEntries.map((entry, idx) => (
                  <EnglishEntryCard
                    key={`${entry.english_word}-${idx}`}
                    entry={entry}
                    onCreoleClick={handleCreoleClick}
                    highlightSearchTerm={highlightSearchTerm}
                    debouncedSearch={englishDebouncedSearch}
                  />
                ))}
              </div>
            )
          ) : isFrenchTab ? (
            /* French Tab Content */
            frenchLoading ? (
              <EntryCardSkeleton />
            ) : frenchEntries.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-dark-card rounded-lg border border-gray-200 dark:border-dark-border">
                <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Aucun mot trouvé</h3>
                <p className="text-gray-500 dark:text-gray-400">
                  {frenchTotalCount === 0
                    ? 'Le dictionnaire français n\'a pas encore été généré.'
                    : 'Essayez d\'ajuster votre recherche ou vos filtres'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <Pagination
                  currentPage={frenchCurrentPage}
                  totalPages={frenchTotalPages}
                  hasPrevious={frenchHasPrevious}
                  hasNext={frenchHasNext}
                  onPageChange={fetchFrenchEntries}
                />
                {frenchEntries.map((entry, idx) => (
                  <FrenchEntryCard
                    key={`${entry.french_word}-${idx}`}
                    entry={entry}
                    onCreoleClick={handleCreoleClick}
                    highlightSearchTerm={highlightSearchTerm}
                    debouncedSearch={frenchDebouncedSearch}
                  />
                ))}
              </div>
            )
          ) : isSpanishTab ? (
            /* Spanish Tab Content */
            spanishLoading ? (
              <EntryCardSkeleton />
            ) : spanishEntries.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-dark-card rounded-lg border border-gray-200 dark:border-dark-border">
                <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No se encontraron palabras</h3>
                <p className="text-gray-500 dark:text-gray-400">
                  {spanishTotalCount === 0
                    ? 'El diccionario español aún no ha sido generado. Estará disponible próximamente.'
                    : 'Intenta ajustar tu búsqueda o filtros'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <Pagination
                  currentPage={spanishCurrentPage}
                  totalPages={spanishTotalPages}
                  hasPrevious={spanishHasPrevious}
                  hasNext={spanishHasNext}
                  onPageChange={fetchSpanishEntries}
                />
                {spanishEntries.map((entry, idx) => (
                  <SpanishEntryCard
                    key={`${entry.spanish_word}-${idx}`}
                    entry={entry}
                    onCreoleClick={handleCreoleClick}
                    highlightSearchTerm={highlightSearchTerm}
                    debouncedSearch={spanishDebouncedSearch}
                  />
                ))}
              </div>
            )
          ) : isPortugueseTab ? (
            /* Portuguese Tab Content */
            portugueseLoading ? (
              <EntryCardSkeleton />
            ) : portugueseEntries.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-dark-card rounded-lg border border-gray-200 dark:border-dark-border">
                <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Nenhuma palavra encontrada</h3>
                <p className="text-gray-500 dark:text-gray-400">
                  {portugueseTotalCount === 0
                    ? 'O dicionário português ainda não foi gerado. Estará disponível em breve.'
                    : 'Tente ajustar sua pesquisa ou filtros'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <Pagination
                  currentPage={portugueseCurrentPage}
                  totalPages={portugueseTotalPages}
                  hasPrevious={portugueseHasPrevious}
                  hasNext={portugueseHasNext}
                  onPageChange={fetchPortugueseEntries}
                />
                {portugueseEntries.map((entry, idx) => (
                  <PortugueseEntryCard
                    key={`${entry.portuguese_word}-${idx}`}
                    entry={entry}
                    onCreoleClick={handleCreoleClick}
                    highlightSearchTerm={highlightSearchTerm}
                    debouncedSearch={portugueseDebouncedSearch}
                  />
                ))}
              </div>
            )
          ) : showAlphabetTable ? (
            /* Alphabet Table */
            <AlphabetTableCard
              playPronunciation={playPronunciation}
              playingEntryId={playingEntryId}
              ttsLoading={ttsLoading}
              ttsError={ttsError}
              isStaff={isStaff}
            />
          ) : (
            /* Creole Tab Content */
            loading ? (
              <EntryCardSkeleton />
            ) : entries.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-dark-card rounded-lg border border-gray-200 dark:border-dark-border">
                <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No words found</h3>
                <p className="text-gray-500 dark:text-gray-400">Try adjusting your search or filters</p>
              </div>
            ) : (
              <div className="space-y-3">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  hasPrevious={hasPrevious}
                  hasNext={hasNext}
                  onPageChange={fetchEntries}
                />
                {groupedEntries.map((item) => {
                  if (item.type === 'group') {
                    return (
                      <GroupedEntryCard
                        key={`group:${item.mainWord}:${item.entries[0].id}`}
                        group={item}
                        expandedEntry={expandedEntry}
                        setExpandedEntry={setExpandedEntry}
                        favorites={favorites}
                        toggleFavorite={toggleFavorite}
                        playPronunciation={playPronunciation}
                        playingEntryId={playingEntryId}
                        ttsLoading={ttsLoading}
                        ttsError={ttsError}
                        navigateToSearch={navigateToSearch}
                        debouncedSearch={debouncedSearch}
                        highlightSearchTerm={highlightSearchTerm}
                        highlightSearchTermInHtml={highlightSearchTermInHtml}
                        expandPOSLabel={expandPOSLabel}
                        formatPOSPill={formatPOSPill}
                        resolvePOSFullName={resolvePOSFullName}
                        getPOSSortOrder={getPOSSortOrder}
                        getPosColorClass={getPosColorClass}
                        isStaff={isStaff}
                        clearSessionAudio={clearSessionAudio}
                      />
                    )
                  }
                  return (
                    <EntryCard
                      key={item.entry.id}
                      entry={item.entry}
                      expandedEntry={expandedEntry}
                      setExpandedEntry={setExpandedEntry}
                      favorites={favorites}
                      toggleFavorite={toggleFavorite}
                      playPronunciation={playPronunciation}
                      playingEntryId={playingEntryId}
                      ttsLoading={ttsLoading}
                      ttsError={ttsError}
                      navigateToSearch={navigateToSearch}
                      debouncedSearch={debouncedSearch}
                      highlightSearchTerm={highlightSearchTerm}
                      highlightSearchTermInHtml={highlightSearchTermInHtml}
                      expandPOSLabel={expandPOSLabel}
                      formatPOSPill={formatPOSPill}
                      resolvePOSFullName={resolvePOSFullName}
                      getPOSSortOrder={getPOSSortOrder}
                      getPosColorClass={getPosColorClass}
                      isStaff={isStaff}
                      clearSessionAudio={clearSessionAudio}
                    />
                  )
                })}
              </div>
            )
          )}

          {/* Bottom Pagination */}
          {!showAlphabetTable && (
            <div className="mt-6">
              {isEnglishTab ? (
                <Pagination
                  currentPage={englishCurrentPage}
                  totalPages={englishTotalPages}
                  hasPrevious={englishHasPrevious}
                  hasNext={englishHasNext}
                  onPageChange={fetchEnglishEntries}
                />
              ) : isFrenchTab ? (
                <Pagination
                  currentPage={frenchCurrentPage}
                  totalPages={frenchTotalPages}
                  hasPrevious={frenchHasPrevious}
                  hasNext={frenchHasNext}
                  onPageChange={fetchFrenchEntries}
                />
              ) : isSpanishTab ? (
                <Pagination
                  currentPage={spanishCurrentPage}
                  totalPages={spanishTotalPages}
                  hasPrevious={spanishHasPrevious}
                  hasNext={spanishHasNext}
                  onPageChange={fetchSpanishEntries}
                />
              ) : isPortugueseTab ? (
                <Pagination
                  currentPage={portugueseCurrentPage}
                  totalPages={portugueseTotalPages}
                  hasPrevious={portugueseHasPrevious}
                  hasNext={portugueseHasNext}
                  onPageChange={fetchPortugueseEntries}
                />
              ) : (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  hasPrevious={hasPrevious}
                  hasNext={hasNext}
                  onPageChange={fetchEntries}
                />
              )}
            </div>
          )}
        </div>
      </div>
      <AlertComponent />
    </>
  )
}
